import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@platform/core/lib/format';
import type { PlatformUserStatus } from '@platform/dao';
import { useDebounced } from '@platform/features/search/useDebounced';
import { useMeQuery, usePlatformAccountsQuery } from '@platform/state/api';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { SysNote } from '../system/SysNote';
import { SysTable } from '../system/SysTable';
import { SystemPage } from '../system/SystemPage';
import { globalRoleKey } from './globalRoles';
import { accountErrorKey } from './accountErrorKey';
import { useAccountActions } from './useAccountActions';
import './admin.css';

const COLUMNS = '28px minmax(180px, 1.4fr) minmax(200px, 1.6fr) 180px 160px 110px';
const STATUSES: readonly PlatformUserStatus[] = ['active', 'deactivated', 'all'];

/**
 * «Облікові записи» (рішення 5 етапу 7; таблиця й панель фільтрів — як «Глобальні ролі» й «Журнал аудиту»): пошук за ім'ям чи поштою, стан
 * (активні — типово, деактивовані, усі), особа, пошта, глобальна роль, стан і дія — «Деактивувати» (не власний запис) чи «Відновити».
 * Видалення немає: ланцюжок журналу аудиту посилається на облікові записи. Лише адміністратор платформи; права перевіряє сервер.
 */
export function AdminAccountsPage() {
  const { t } = useTranslation();
  const lang = useAppSelector(selectLang);
  const { data: me } = useMeQuery();
  const admin = me?.globalRole === 'Platform admin';
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<PlatformUserStatus>('active');
  const query = useDebounced(q.trim(), 250);
  const { data, error } = usePlatformAccountsQuery({ q: query, status }, { skip: !admin });
  const { busy, toggle } = useAccountActions();
  const users = data ?? [];
  const rows = users.map((u) => {
    const active = u.active !== false;
    const self = u.id === me?.id;
    return {
      key: u.id,
      cells: [
        <span key="a" className="sp-avatar">{u.initials}</span>,
        <span key="n" className="sp-who"><span className={active ? 'sp-strong ad-ellipsis' : 'sp-strong ad-ellipsis sp-muted'}>{u.name}</span>{self && <span className="sp-badge">{t('sysAccounts.you')}</span>}</span>,
        <span key="e" className="sp-muted">{u.email ?? ''}</span>,
        <span key="r">{t(`sysAdmin.global.${globalRoleKey(u.globalRole)}`)}</span>,
        <span key="s" className={active ? 'ad-state is-on' : 'ad-state'}><span className="ad-state__dot" aria-hidden="true" />
          {active ? t('sysAccounts.active') : u.deactivatedAt ? t('sysAccounts.deactivatedAt', { when: formatDate(u.deactivatedAt, lang) }) : t('sysAccounts.deactivated')}</span>,
        <span key="x" className="sp-actions">
          <button type="button" className={active ? 'ad-rowbtn ad-rowbtn--danger' : 'ad-rowbtn'} disabled={busy === u.id || (active && self)}
            title={active && self ? t('sysAccounts.self') : undefined} aria-label={t(active ? 'sysAccounts.deactivateOf' : 'sysAccounts.reactivateOf', { name: u.name })} onClick={() => toggle(u)}>
            {t(active ? 'sysAccounts.deactivate' : 'sysAccounts.reactivate')}
          </button>
        </span>,
      ],
    };
  });
  return (
    <SystemPage title={t('sysPages.sections.accounts')} sub={t('sysAccounts.sub')}>
      {me && !admin && <SysNote tone="alert">{t('sysAccounts.locked')}</SysNote>}
      {admin && (
        <>
          <div className="sp-toolbar">
            <input className="sp-input ad-search" value={q} placeholder={t('sysAccounts.search')} aria-label={t('sysAccounts.searchLabel')} onChange={(e) => setQ(e.target.value)} />
            <select className="sp-select ad-filter" value={status} aria-label={t('sysAccounts.statusLabel')} onChange={(e) => setStatus(e.target.value as PlatformUserStatus)}>
              {STATUSES.map((s) => <option key={s} value={s}>{t(`sysAccounts.status.${s}`)}</option>)}
            </select>
            {data && <span className="sp-count">{t('sysAccounts.count', { count: users.length })}</span>}
          </div>
          {error && <SysNote tone="alert">{t(accountErrorKey(error))}</SysNote>}
          <div className="ad-accounts">
            <SysTable columns={COLUMNS} minWidth={860} rows={rows} empty={data ? t('sysAccounts.none') : undefined} label={t('sysPages.sections.accounts')}
              head={['', t('sysAccounts.person'), t('sysAccounts.email'), t('sysAccounts.role'), t('sysAccounts.state'), '']} />
          </div>
        </>
      )}
    </SystemPage>
  );
}
